import { createFileRoute, Link, redirect } from '@tanstack/react-router';
import { CodeBlock, InfoCallout } from '../../components/documentation';
import styles from '../../components/documentation/DocContent.module.css';
import tutorialStyles from './Tutorial.module.css';

export const Route = createFileRoute('/documentation/tutorial-react')({
  beforeLoad: () => {
    throw redirect({ to: '/documentation/tutorial' });
  },
  component: TutorialReactPage,
});

function TutorialReactPage() {
  return (
    <div className={tutorialStyles.page}>
      <div className={styles.breadcrumb}>Getting Started</div>
      <h1 className={styles.pageTitle}>Stream in React</h1>
      <p className={styles.pageSubtitle}>
        Add a GPU-rendered stream to a React app in about five minutes.
      </p>

      <div className={tutorialStyles.badge}>
        <span>~5 min</span>
        <span className={tutorialStyles.badgeSep} />
        <span>3 steps</span>
      </div>

      {/* ── Video placeholder ── */}
      <div className={tutorialStyles.videoPlaceholder}>
        <div className={tutorialStyles.videoPlayIcon}>
          <div className={tutorialStyles.videoPlayTriangle} />
        </div>
        <span className={tutorialStyles.videoLabel}>
          Watch the framework walkthrough (4:30)
        </span>
      </div>

      {/* ── Prerequisites ── */}
      <section className={styles.section}>
        <h2 className={styles.sectionTitle} id="prerequisites">
          Before You Begin
        </h2>
        <ul className={tutorialStyles.prereqList}>
          <li className={tutorialStyles.prereqItem}>
            <span className={tutorialStyles.prereqCheck} />
            <span>
              <span className={styles.emphasis}>Node.js 18+</span> and npm
              (or pnpm / yarn)
            </span>
          </li>
          <li className={tutorialStyles.prereqItem}>
            <span className={tutorialStyles.prereqCheck} />
            <span>
              An <span className={styles.emphasis}>admission token</span> — see
              the{' '}
              <Link to="/documentation/tutorial" hash="prerequisites">
                HTML tutorial prerequisites
              </Link>
            </span>
          </li>
          <li className={tutorialStyles.prereqItem}>
            <span className={tutorialStyles.prereqCheck} />
            <span>
              Familiarity with React function components and hooks
            </span>
          </li>
        </ul>
        <InfoCallout variant="tip">
          New to pixel-stream? Start with the{' '}
          <Link to="/documentation/tutorial">HTML tutorial</Link> first — it
          covers the core concepts without any framework overhead.
        </InfoCallout>
      </section>

      {/* ── Step 1 ── */}
      <section className={styles.section}>
        <h2 className={styles.sectionTitle} id="scaffold-the-project">
          Step 1 — Scaffold the Project
        </h2>
        <p className={styles.prose}>
          Create a new Vite + React + TypeScript project and install the
          pixel-stream package:
        </p>
        <CodeBlock language="bash" title="Terminal">
{`npm create vite@latest my-stream -- --template react-ts
cd my-stream
npm install @interlucent/pixel-stream`}
        </CodeBlock>
      </section>

      {/* ── Step 2 ── */}
      <section className={styles.section}>
        <h2 className={styles.sectionTitle} id="add-the-stream">
          Step 2 — Add the Stream Component
        </h2>
        <p className={styles.prose}>
          React's JSX type system does not know about custom elements. Create a
          type declaration file so TypeScript accepts the{' '}
          <code>&lt;pixel-stream&gt;</code> tag:
        </p>
        <CodeBlock language="typescript" title="src/pixel-stream.d.ts">
{`import type { DetailedHTMLProps, HTMLAttributes } from 'react';

declare global {
  namespace JSX {
    interface IntrinsicElements {
      'pixel-stream': DetailedHTMLProps<
        HTMLAttributes<HTMLElement> & {
          controls?: boolean;
          'admission-token'?: string;
          'reconnect-mode'?: 'none' | 'recover' | 'always';
          'swift-job-request'?: boolean;
          'queue-wait-tolerance'?: number | string;
          'webrtc-negotiation-tolerance'?: number | string;
          debug?: boolean;
        },
        HTMLElement
      >;
    }
  }
}`}
        </CodeBlock>
        <p className={styles.prose}>
          Now replace <code>src/App.tsx</code> with the streaming component. The{' '}
          <code>import</code> registers the custom element, and the{' '}
          <code>ref</code> gives you a handle for setting the token and calling
          methods:
        </p>
        <CodeBlock language="tsx" title="src/App.tsx">
{`import { useRef, useEffect } from 'react';
import '@interlucent/pixel-stream';

function App() {
  const psRef = useRef<HTMLElement>(null);

  useEffect(() => {
    if (psRef.current) {
      (psRef.current as any).admissionToken = 'YOUR_TOKEN';
    }
  }, []);

  return (
    <pixel-stream
      ref={psRef}
      controls
      style={{ display: 'block', width: '100vw', height: '100vh' }}
    />
  );
}

export default App;`}
        </CodeBlock>
        <InfoCallout variant="info">
          React (before v19) does not forward unknown props as DOM properties.
          Setting <code>admissionToken</code> via a ref ensures the value is
          set as a JavaScript property, not a stringified attribute.
        </InfoCallout>
      </section>

      {/* ── Step 3 ── */}
      <section className={styles.section}>
        <h2 className={styles.sectionTitle} id="run-and-play">
          Step 3 — Run and Play
        </h2>
        <CodeBlock language="bash" title="Terminal">
{`npm run dev`}
        </CodeBlock>
        <p className={styles.prose}>
          Open the URL shown in your terminal. You will see a dark viewport with
          a <span className={styles.emphasis}>play button</span>. Click it —
          the component connects to the platform, requests a GPU worker, and
          starts streaming. Move your mouse to interact. That's it — you're
          streaming from React.
        </p>
        <InfoCallout variant="warning">
          If nothing appears, open the browser console (F12). The most common
          issue is an expired admission token — generate a fresh one and
          replace the value in <code>App.tsx</code>.
        </InfoCallout>
      </section>

      {/* ── Make It Yours ── */}
      <section className={styles.section}>
        <h2 className={styles.sectionTitle} id="make-it-yours">
          Make It Yours
        </h2>

        <h3 className={styles.subsectionTitle} id="custom-overlay">
          Custom Overlay
        </h3>
        <p className={styles.prose}>
          Replace the built-in controls with your own React component using the{' '}
          <code>overlay</code> slot. The <code>&lt;pixel-stream&gt;</code>{' '}
          element sets a <code>status</code> attribute on itself as the session
          progresses — <code>idle</code>, <code>connecting</code>,{' '}
          <code>queued</code>, <code>streaming</code>, <code>failed</code>,
          etc. Use CSS attribute selectors to show the right state:
        </p>
        <CodeBlock language="tsx" title="StreamOverlay.tsx">
{`function StreamOverlay({ psRef }: { psRef: React.RefObject<HTMLElement | null> }) {
  const ps = () => psRef.current as any;

  return (
    <div slot="overlay" className="overlay">
      <div className="state state-idle">
        <button onClick={() => ps()?.play()}>Launch Experience</button>
      </div>
      <div className="state state-pending">
        <div className="spinner" />
        <p>Preparing your session\u2026</p>
        <button onClick={() => ps()?.cancel()}>Cancel</button>
      </div>
      <div className="state state-error">
        <p>Something went wrong.</p>
        <button onClick={() => ps()?.play()}>Try Again</button>
      </div>
    </div>
  );
}`}
        </CodeBlock>
        <CodeBlock language="css" title="overlay.css">
{`.state { display: none; }

pixel-stream[status="idle"]           .state-idle,
pixel-stream[status="connected"]      .state-idle    { display: flex; }

pixel-stream[status="connecting"]     .state-pending,
pixel-stream[status="authenticating"] .state-pending,
pixel-stream[status="queued"]         .state-pending,
pixel-stream[status="negotiating"]    .state-pending { display: flex; }

pixel-stream[status="failed"]        .state-error,
pixel-stream[status="ended"]         .state-error   { display: flex; }

pixel-stream[status="streaming"]     .overlay { display: none; }`}
        </CodeBlock>
        <InfoCallout variant="info">
          With CSS Modules, prefix the attribute selector with{' '}
          <code>:global()</code> — e.g.{' '}
          <code>{`:global(pixel-stream[status="idle"]) .stateIdle`}</code>.
          See the{' '}
          <Link to="/documentation/streaming-guide" hash="custom-overlay">
            Streaming Guide
          </Link>{' '}
          for the full overlay pattern.
        </InfoCallout>

        <h3 className={styles.subsectionTitle} id="listen-for-events">
          Listen for Events
        </h3>
        <p className={styles.prose}>
          React's synthetic event system does not capture custom element events.
          Use <code>addEventListener</code> on the ref:
        </p>
        <CodeBlock language="tsx" title="events via useEffect">
{`useEffect(() => {
  const ps = psRef.current;
  if (!ps) return;

  const onStatus = (e: Event) => {
    const { newStatus, failureReason } = (e as CustomEvent).detail;
    console.log('Status:', newStatus, failureReason);
  };

  ps.addEventListener('status-change', onStatus);
  return () => ps.removeEventListener('status-change', onStatus);
}, []);`}
        </CodeBlock>

        <h3 className={styles.subsectionTitle} id="send-a-message">
          Send a Message
        </h3>
        <CodeBlock language="tsx" title="messaging via ref">
{`useEffect(() => {
  const ps = psRef.current;
  if (!ps) return;

  ps.addEventListener('data-channel-open', () => {
    (ps as any).sendUIInteraction({
      action: 'setColor',
      target: 'car-body',
      color: '#FF5500',
    });
  });
}, []);`}
        </CodeBlock>
        <InfoCallout variant="warning">
          Always wait for the <code>data-channel-open</code> event before
          calling <code>sendUIInteraction()</code> or{' '}
          <code>sendCommand()</code>.
        </InfoCallout>
      </section>

      {/* ── Next Steps ── */}
      <section className={styles.section}>
        <h2 className={styles.sectionTitle} id="next-steps">
          Next Steps
        </h2>
        <div className={styles.linkGrid}>
          <Link
            to="/documentation/streaming-guide"
            className={styles.linkCard}
          >
            <div className={styles.linkCardTitle}>Streaming Guide</div>
            <div className={styles.linkCardDesc}>
              Configuration, custom overlays with CSS status selectors,
              bidirectional messaging, and a full configurator example.
            </div>
          </Link>
          <Link to="/documentation/tutorial" className={styles.linkCard}>
            <div className={styles.linkCardTitle}>HTML Tutorial</div>
            <div className={styles.linkCardDesc}>
              The framework-free version — CDN script tag, no build step.
              Covers the same concepts without React overhead.
            </div>
          </Link>
          <Link
            to="/documentation/sdk-reference"
            className={styles.linkCard}
          >
            <div className={styles.linkCardTitle}>SDK Reference</div>
            <div className={styles.linkCardDesc}>
              Generate admission tokens server-side. Minimal, production,
              low-latency, and resilient token patterns.
            </div>
          </Link>
        </div>
      </section>
    </div>
  );
}
